import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Card, Col, Row } from 'react-bootstrap';
import DataLoadError from '@/components/errors/DataLoadError';
import classNames from 'classnames';
import DrawerWrapper from '@/app/common/components/DrawerWrapper';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import IconItem from '@/components/common/icon/IconItem';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { ModelIcon } from '../ModelIcon';
import { AiResponseText } from '../prompts/AiResponseText';
import { BrandsTable } from '../BrandsTable';
import { KpiStatCard } from '../common/KpiStatCard';
import { KpiCardsSkeleton } from '../common/Skeleton';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { SectionTitle } from '../help';
import { formatExecutionVisibility } from '../../utils/execution-filters';
import { getHelpHtml } from '../../utils/help/help-content';
import { timeAgo } from '../../utils/format';
import { ExecutionSourceBadge } from './ExecutionSourceBadge';
import type { ExecutionInsight } from '../../types';

function formatDateTimeFa(iso: string): ReactNode {
  const d = new Date(iso);
  const date = d.toLocaleDateString('fa-IR');
  const time = d.toLocaleTimeString('fa-IR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return (
    <span className="text-nowrap">
      {date} {time}
    </span>
  );
}

function PromptMetaBadge({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span className="badge badge-subtle-secondary rounded-pill" title={title}>
      {children}
    </span>
  );
}

function formatDurationFa(ms: number | null): string {
  if (ms == null || ms < 0) return '—';
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} ثانیه`;
  const mins = Math.floor(seconds / 60);
  const rem = Math.round(seconds % 60);
  return `${mins} دقیقه و ${rem} ثانیه`;
}

type TimelineStepStatus = 'done' | 'active' | 'pending' | 'failed' | 'skipped';

interface TimelineStep {
  id: string;
  label: string;
  status: TimelineStepStatus;
  at?: string | null;
}

function MetadataSection({
  execution,
  loading,
}: {
  execution: ExecutionInsight | null;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <Card className="mb-0 placeholder-glow">
        <FalconCardHeader light titleTag="h6" className="py-2" title="متادیتا" />
        <Card.Body className="d-flex flex-column gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i} className="placeholder rounded d-block" style={{ height: 20 }} />
          ))}
        </Card.Body>
      </Card>
    );
  }

  if (!execution) return null;

  const startedAt = formatDateTimeFa(execution.executedAt);
  const finishedAt = execution.finishedAt ? formatDateTimeFa(execution.finishedAt) : null;
  const durationMs =
    execution.finishedAt != null
      ? new Date(execution.finishedAt).getTime() - new Date(execution.executedAt).getTime()
      : null;

  const rows: Array<{ label: string; value: ReactNode }> = [
    {
      label: 'مدل',
      value: execution.model ? (
        <span className="d-inline-flex align-items-center gap-2">
          <ModelIcon svg={execution.modelIconSvg} name={execution.model} size={18} />
          <span>{execution.model}</span>
        </span>
      ) : (
        execution.provider ?? '—'
      ),
    },
    {
      label: 'منبع',
      value: <ExecutionSourceBadge source={execution.executionSource ?? 'manual'} />,
    },
    { label: 'شروع', value: startedAt },
    { label: 'پایان', value: finishedAt ?? '—' },
    { label: 'مدت', value: formatDurationFa(durationMs) },
  ];

  return (
    <Card className="mb-0">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-2"
        title={
          <span className="d-inline-flex align-items-center gap-2">
            <IconItem
              tag="div"
              icon="info"
              bg="soft-secondary"
              color="secondary"
              size="sm"
              iconClass="fs--2"
              className="shadow-none"
            />
            متادیتا
          </span>
        }
      />
      <dl className="row g-0 mb-0">
        {rows.map(row => (
          <div key={row.label} className="col-12 border-bottom border-200">
            <div className="d-flex align-items-center justify-content-between gap-3 px-3 py-2">
              <dt className="flex-shrink-0 fs--1 text-500 mb-0">{row.label}</dt>
              <dd
                className="min-w-0 fs--1 fw-medium text-1000 text-end mb-0"
                style={{ wordBreak: 'break-word' }}
              >
                {row.value}
              </dd>
            </div>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function buildTimeline(execution: ExecutionInsight | null): TimelineStep[] {
  if (!execution) {
    return [
      { id: 'queued', label: 'در صف', status: 'pending' },
      { id: 'started', label: 'شروع شده', status: 'pending' },
      { id: 'ai', label: 'درخواست AI', status: 'pending' },
      { id: 'extract', label: 'شناسایی برند', status: 'pending' },
      { id: 'visibility', label: 'محاسبه دیده‌شدن', status: 'pending' },
      { id: 'completed', label: 'تکمیل شده', status: 'pending' },
    ];
  }

  const status = execution.status.toLowerCase();
  const failed = status === 'failed' || status === 'error';
  const cancelled = status === 'cancelled' || status === 'canceled';
  const completed = status === 'completed' || status === 'executed' || status === 'success';
  const running = status === 'running' || status === 'processing' || status === 'in_progress';
  const queued = status === 'queued' || status === 'pending';
  const hasBrands = execution.brands.length > 0;

  return [
    { id: 'queued', label: 'در صف', status: queued ? 'active' : 'done', at: execution.executedAt },
    { id: 'started', label: 'شروع شده', status: queued ? 'pending' : 'done', at: execution.executedAt },
    {
      id: 'ai',
      label: 'درخواست AI',
      status: queued
        ? 'pending'
        : running
          ? 'active'
          : failed && !execution.responseText
            ? 'failed'
            : cancelled
              ? 'failed'
              : execution.responseText || completed || failed
                ? 'done'
                : 'pending',
      at: execution.executedAt,
    },
    {
      id: 'extract',
      label: 'شناسایی برند',
      status: hasBrands ? 'done' : queued || running ? 'pending' : failed ? 'skipped' : 'pending',
      at: execution.finishedAt,
    },
    {
      id: 'visibility',
      label: 'محاسبه دیده‌شدن',
      status: hasBrands || completed ? 'done' : queued || running ? 'pending' : 'skipped',
    },
    {
      id: 'completed',
      label: cancelled ? 'لغو شده' : failed ? 'ناموفق' : 'تکمیل شده',
      status: completed ? 'done' : failed || cancelled ? 'failed' : running ? 'active' : 'pending',
      at: execution.finishedAt ?? execution.executedAt,
    },
  ];
}

const TIMELINE_DOT: Record<TimelineStepStatus, string> = {
  done: 'bg-success',
  active: 'bg-primary',
  failed: 'bg-danger',
  pending: 'bg-300',
  skipped: 'bg-300',
};

function TimelineView({ steps, loading }: { steps: TimelineStep[]; loading?: boolean }) {
  if (loading) {
    return (
      <div className="d-flex flex-column gap-2 placeholder-glow">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className="placeholder rounded d-block" style={{ height: 22 }} />
        ))}
      </div>
    );
  }

  return (
    <ol className="list-unstyled mb-0">
      {steps.map((step, index) => (
        <li key={step.id} className={`position-relative d-flex gap-2 ${index < steps.length - 1 ? 'pb-2' : ''}`}>
          {index < steps.length - 1 && (
            <span
              className="position-absolute bg-300"
              style={{ insetInlineStart: 7, top: 16, width: 1, height: 'calc(100% - 6px)' }}
            />
          )}
          <span
            className={classNames(
              'position-relative mt-1 flex-shrink-0 rounded-circle',
              TIMELINE_DOT[step.status],
              step.status === 'pending' || step.status === 'skipped' ? 'opacity-50' : '',
            )}
            style={{ width: 14, height: 14, zIndex: 1 }}
          />
          <div className="min-w-0 flex-grow-1">
            <p
              className={classNames('fs--1 mb-0', {
                'fw-medium text-800': step.status === 'done' || step.status === 'active',
                'text-danger': step.status === 'failed',
                'text-500': step.status === 'pending' || step.status === 'skipped',
              })}
            >
              {step.label}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function ExecutionDrawer({
  executionId,
  projectId,
  onClose,
}: {
  executionId: string;
  projectId?: string;
  onClose: () => void;
}) {
  const [execution, setExecution] = useState<ExecutionInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setShowDebug(false);

    api
      .getExecutionInsight(executionId)
      .then(data => {
        if (!cancelled) {
          setExecution(data as ExecutionInsight);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setExecution(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [executionId, retryNonce]);

  const timeline = useMemo(() => buildTimeline(execution), [execution]);
  const isFailed =
    execution != null &&
    (execution.status.toLowerCase() === 'failed' || execution.status.toLowerCase() === 'error');

  const drawerTitle = (
    <div className="min-w-0 pe-2">
      <SectionTitle title="جزئیات اجرا" />
    </div>
  );

  const modelLabel = execution?.model ?? execution?.provider;

  return (
    <DrawerWrapper show title={drawerTitle} onHide={onClose} width={1080}>
      <div className="d-flex flex-column gap-3 pt-2">
        {error ? (
          <DataLoadError
            compact
            onRetry={() => setRetryNonce(n => n + 1)}
            isRetrying={loading}
          />
        ) : (
          <>
        {isFailed && (
          <Alert variant="danger" className="fs--1 mb-0">
            اجرا ناموفق بود
          </Alert>
        )}

        <Card>
          <Card.Body className="d-flex flex-column gap-3">
            {loading ? (
              <span className="placeholder rounded d-block" style={{ height: 56 }} />
            ) : (
              <p className="fs-0 text-800 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {execution?.promptText?.trim() || 'متن پرامپت در دسترس نیست.'}
              </p>
            )}
            {loading ? (
              <span className="placeholder rounded d-block" style={{ width: 220, height: 20 }} />
            ) : execution ? (
              <div className="d-flex flex-wrap align-items-center gap-2">
                {execution.topicName ? (
                  <PromptMetaBadge>{execution.topicName}</PromptMetaBadge>
                ) : null}
                {modelLabel ? (
                  <PromptMetaBadge>
                    <span className="d-inline-flex align-items-center gap-1">
                      {execution.model ? (
                        <ModelIcon svg={execution.modelIconSvg} name={execution.model} size={12} />
                      ) : null}
                      {modelLabel}
                    </span>
                  </PromptMetaBadge>
                ) : null}
                <PromptMetaBadge title={new Date(execution.executedAt).toLocaleString('fa-IR')}>
                  {timeAgo(execution.executedAt)}
                </PromptMetaBadge>
              </div>
            ) : null}
          </Card.Body>
        </Card>

        {loading ? (
          <KpiCardsSkeleton count={3} cols={{ xs: 4, lg: 4 }} />
        ) : (
          <Row className="g-3">
            <Col xs={12} sm={4}>
              <KpiStatCard
                imageIndex={0}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    دیده‌شدن در AI
                    <HelpTooltip html={getHelpHtml('ai_visibility')} />
                  </span>
                }
                value={
                  execution
                    ? formatExecutionVisibility({
                        status: execution.status,
                        visibilityScore: execution.metrics.visibilityScore,
                        brandsFound: execution.metrics.brandsFound,
                      })
                    : '—'
                }
                valueClassName="text-warning"
                valueAnnotation="سهم برند شما در این اجرا"
              />
            </Col>
            <Col xs={12} sm={4}>
              <KpiStatCard
                imageIndex={1}
                label="میانگین رتبه"
                value={execution?.metrics.avgPosition ?? '—'}
                valueClassName="text-info"
                valueAnnotation="رتبه برند شما در این اجرا"
              />
            </Col>
            <Col xs={12} sm={4}>
              <KpiStatCard
                imageIndex={2}
                label="برندهای یافت‌شده"
                value={execution?.metrics.brandsFound ?? 0}
                valueClassName="text-success"
                valueAnnotation="برند در پاسخ این اجرا"
              />
            </Col>
          </Row>
        )}

        <Row className="g-3">
          <Col xs={12} lg={8}>
            {loading ? (
              <AgGridSkeleton height="320px" />
            ) : (
              <Card className="h-100 overflow-hidden ag-grid-in-card mb-0">
                <FalconCardHeader
                  light
                  titleTag="h6"
                  className="py-2"
                  title={
                    <span className="d-inline-flex align-items-center gap-2">
                      <IconItem
                        tag="div"
                        icon="tags"
                        bg="soft-info"
                        color="info"
                        size="sm"
                        iconClass="fs--2"
                        className="shadow-none"
                      />
                      برندهای شناسایی‌شده
                      <HelpTooltip html={getHelpHtml('detected_brands')} />
                    </span>
                  }
                  endEl={
                    execution?.brands.length ? (
                      <button
                        type="button"
                        onClick={() => setShowDebug(value => !value)}
                        className="btn btn-link p-0 fs--1 text-primary text-decoration-none"
                      >
                        {showDebug ? 'مخفی کردن دیباگ' : 'نمایش دیباگ'}
                      </button>
                    ) : null
                  }
                />
                <Card.Body>
                  {!execution?.brands.length ? (
                    <div className="rounded-3 border border-dashed bg-body-tertiary px-3 py-4 text-center fs--1 text-500">
                      {isFailed
                        ? 'به‌دلیل ناموفق بودن اجرا، برندی شناسایی نشد.'
                        : 'برندی برای این اجرا شناسایی نشد.'}
                    </div>
                  ) : (
                    <BrandsTable
                      brands={execution.brands}
                      projectId={projectId}
                      showHeader={false}
                      showDebugToggle={false}
                      debug={showDebug}
                      onDebugChange={setShowDebug}
                    />
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
          <Col xs={12} lg={4} className="d-flex flex-column gap-3">
            <Card className="mb-0">
              <FalconCardHeader
                light
                titleTag="h6"
                className="py-2"
                title={
                  <span className="d-inline-flex align-items-center gap-2">
                    <IconItem
                      tag="div"
                      icon="stream"
                      bg="soft-primary"
                      color="primary"
                      size="sm"
                      iconClass="fs--2"
                      className="shadow-none"
                    />
                    خط زمانی
                  </span>
                }
              />
              <Card.Body className="py-3">
                <TimelineView steps={timeline} loading={loading} />
              </Card.Body>
            </Card>
            <MetadataSection execution={execution} loading={loading} />
          </Col>
        </Row>

        {!loading && execution?.responseText?.trim() ? (
          <Card className="mb-0 overflow-hidden">
            <FalconCardHeader
              light
              titleTag="h6"
              className="py-2"
              title={
                <span className="d-inline-flex align-items-center gap-2">
                  <IconItem
                    tag="div"
                    icon="comment"
                    bg="soft-success"
                    color="success"
                    size="sm"
                    iconClass="fs--2"
                    className="shadow-none"
                  />
                  پاسخ AI
                </span>
              }
            />
            <Card.Body>
              <AiResponseText
                text={execution.responseText}
                promptText={execution.promptText}
                modelLabel={execution.model ?? undefined}
                modelIconSvg={execution.modelIconSvg}
                projectId={projectId}
                showToolbar
              />
            </Card.Body>
          </Card>
        ) : null}
          </>
        )}
      </div>
    </DrawerWrapper>
  );
}
